import { useEffect, useRef } from "react";
import { api, jsonBody } from "../api/client";
import type { Profile } from "../api/types";
import { useAuth } from "../auth/AuthProvider";
import {
  DEFAULT_PALETTE,
  DEFAULT_THEME,
  isPalette,
  isTheme,
  type Palette,
  type Theme,
  useTheme,
} from "./ThemeProvider";

// Everything a person owns about themselves, and the whole of it every time. The endpoint rewrites
// the record rather than merging into it, so anything not sent is not preserved: a caller changing
// one field has to carry the rest, and the rest it carries is what the account currently holds
// rather than whatever happens to be typed into a form. Changing a color must not save half a name
// somebody is still in the middle of correcting.
export interface ProfileDetails {
  displayName: string;
  locale: string;
  theme: Theme;
  palette: Palette;
  avatarUrl: string;
  signature: string;
}

// The stored record read as something the product can actually draw. A theme or palette the browser
// no longer recognizes becomes the default here rather than being handed back to the server
// unchanged, because the server would refuse it and the person would be told their own profile is
// invalid over a value they never chose and cannot see.
export function profileDetails(profile: Profile): ProfileDetails {
  return {
    displayName: profile.displayName,
    locale: profile.locale,
    theme: isTheme(profile.theme) ? profile.theme : DEFAULT_THEME,
    palette: isPalette(profile.palette) ? profile.palette : DEFAULT_PALETTE,
    avatarUrl: profile.avatarUrl,
    signature: profile.signature,
  };
}

export function saveProfile(details: ProfileDetails): Promise<Profile> {
  return api<Profile>("/api/v1/auth/profile", { method: "PUT", body: jsonBody(details) });
}

// The account decides how the product looks; the device only decides how the first frame looks.
//
// Browser storage still paints that first frame, because it is the only answer available before
// anybody is known, and it is the right answer for the sign-in page. The moment a session says who
// this is, what they kept wins: their own colors follow them onto a machine they have never used,
// which is the entire difference between a preference and a setting on one computer.
//
// Once per person, not once per session read. The session record is refetched whenever anything
// about it changes, and adopting on every read would drag the appearance back to the stored value a
// beat after somebody changed it, which reads as the product arguing with them.
export function AccountAppearance() {
  const auth = useAuth();
  const { apply } = useTheme();
  const adoptedFor = useRef<string | null>(null);
  const profile = auth.session?.user;
  useEffect(() => {
    if (!profile) {
      // A session ending here rather than through a reload still has to be forgotten, or the next
      // person to sign in on this browser inherits whichever colors were last adopted.
      adoptedFor.current = null;
      return;
    }
    if (adoptedFor.current === profile.id) return;
    adoptedFor.current = profile.id;
    const details = profileDetails(profile);
    apply(details.theme, details.palette);
  }, [profile, apply]);
  return null;
}

// Applying a choice and recording it are separate steps here for the same reason they are separate
// in the theme provider, and they happen in this order on purpose: the product repaints at once
// rather than after a round trip, and a slow or failed write never leaves somebody staring at the
// colors they just rejected.
export function useSaveAppearance(): (next: { theme?: Theme; palette?: Palette }) => Promise<void> {
  const auth = useAuth();
  const { theme, palette, apply } = useTheme();
  return async (next) => {
    const chosenTheme = next.theme ?? theme;
    const chosenPalette = next.palette ?? palette;
    apply(chosenTheme, chosenPalette);
    const profile = auth.session?.user;
    // Nobody is signed in, so this browser is the only place a preference could live, and it already
    // does. The sign-in page has colors too.
    if (!profile) return;
    await saveProfile({ ...profileDetails(profile), theme: chosenTheme, palette: chosenPalette });
    await auth.refresh();
  };
}
